import {
  Divider,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm } from "react-hook-form";

import { useOrgCreateMutation } from "@/app/data/useOrgCreateMutation";
import { useOrgsData } from "@/app/data/useOrgsData";
import GoToHome from "@/auth/components/GoToHome";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useFormFieldUpdate } from "@/common/hooks/useFormFieldUpdate";
import { SETUP_DEMO_DATA } from "@/common/utils/env";
import { CreateOrgArgsSchema } from "@reflag/shared/organizationAPI";
import { GenerateDemoAppUrl } from "@reflag/shared/urls";

export default function GetStarted() {
  const { user, org } = useEnvironmentUnsafe() ?? {};

  if (org) {
    return <GoToHome />;
  }

  return (
    <>
      <Helmet>
        <title>Get Started</title>
      </Helmet>
      <Flex direction="column" gap={4} w="sm">
        <Flex direction="row" gap={4}>
          <Heading fontSize="2xl">Welcome {user?.name.split(" ")[0]}</Heading>
        </Flex>
        <Text color="dimmed">
          Get started by creating your organization on Reflag
        </Text>
        <Divider />
        <Form />
      </Flex>
    </>
  );
}

function Form() {
  const { org } = useEnvironmentUnsafe() ?? {};
  const queryClient = useQueryClient();
  const bootstrapQuery = queryClient.getQueryState(
    commonQueryKeys.bootstrap(org?.id),
  );
  const { setActiveOrg } = useEnvironmentUnsafe() ?? {};
  const { fetchStatus } = bootstrapQuery ?? {};

  const form = useForm({
    resolver: zodResolver(CreateOrgArgsSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      app: {
        name: "",
      },
    },
  });

  const nameSuggestion = useOrgNameSuggestion();

  useEffect(() => {
    if (nameSuggestion) {
      form.setValue("name", nameSuggestion);

      form.setValue("app.name", nameSuggestion, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [nameSuggestion, form]);

  const orgCreateMutation = useOrgCreateMutation();

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    orgCreateMutation,
    (org) => {
      const env = org.apps?.[0]?.environments?.[0];
      if (!env) {
        console.error("[GetStarted] No app found in newly created org");
        return;
      }

      if (SETUP_DEMO_DATA) {
        setActiveOrg?.(org.id, { refresh: false });

        // we're purposefully doing a full page reload here to ensure the
        // the bootstrap query is re-run and the the org is loaded correctly
        // so as to avoid getting sent back here.
        window.location.assign(GenerateDemoAppUrl(org));
      } else {
        if (setActiveOrg) {
          setActiveOrg(org.id, { refresh: true });
        } else {
          window.location.assign("/");
        }
      }
    },
  );

  // generate app name from org name as you type
  useFormFieldUpdate(form, "name", (orgName) => {
    form.setValue("app.name", `${orgName}`);
  });

  // keep form disabled after successful submit while we redirect
  const isDisabled =
    form.formState.isSubmitSuccessful || fetchStatus === "fetching";

  return (
    <form onSubmit={handleSubmit}>
      <FormProvider {...form}>
        <VStack align="flex-start" spacing={4}>
          <ManagedFormControl
            label="Organization name"
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                autoComplete="organization"
                maxLength={40}
                placeholder="Acme Inc."
                size="md"
                autoFocus
              />
            )}
          />
          <DomainJoinBanner />
          <FormRootError />
          <HStack spacing={4}>
            <FormSubmit
              isDisabled={isDisabled}
              loadingText="Creating..."
              size="md"
            >
              Let&apos;s go
            </FormSubmit>
          </HStack>
        </VStack>
      </FormProvider>
    </form>
  );
}

function useOrgNameSuggestion(): string | undefined {
  const { user } = useEnvironmentUnsafe() ?? {};

  const domainNamePart = user?.domain?.split(".")[0];

  if (!domainNamePart) {
    return undefined;
  }

  // We're ignoring possibly puny-coded domains
  // Replace all non-numbers and non-letters with space
  const suggestion = domainNamePart.replace(/[^0-9A-Za-z]/g, " ");

  // Uppercase first letter
  return suggestion.charAt(0).toLocaleUpperCase() + suggestion.slice(1);
}

function DomainJoinBanner() {
  const { user } = useEnvironmentUnsafe() ?? {};
  const { isPending, data } = useOrgsData();

  if (!user?.domain) {
    return null;
  }

  if (isPending) {
    return <Spinner />;
  }

  if (!data?.metadata?.userDomainAutoJoinEligible) {
    return null;
  }

  if (data.metadata.orgsWithUserDomainExists) {
    return (
      <Text color="dimmed" fontSize="sm">
        An organization connected with the{" "}
        <Text as="span" color="chakra-body-text" fontWeight="semibold">
          {user.domain}
        </Text>{" "}
        domain already exists. Consider asking an admin to invite you to that
        organization instead of creating a new organization on Reflag.
      </Text>
    );
  }

  return (
    <Text color="dimmed" fontSize="sm">
      Users that sign up with an{" "}
      <Text as="span" color="chakra-body-text" fontWeight="semibold">
        @{user.domain}
      </Text>{" "}
      email will automatically join your organization
    </Text>
  );
}
